'use es6';

import debounce from 'react-utils/debounce';
import { VideoOverlayPositions, VideoSizes } from 'rich-text-lib/constants/video';
import { Alignments } from 'rich-text-lib/constants/alignments';
import { isHubLVideo, isHubSpotVideo2, getHubLVideoHubLTag, getHubLVideoParamObjectFromFile } from 'FileManagerLib/utils/hubLVideo';
import { STYLE_OPTIONS } from '../constants/hsMedia';
import { HublVideoTypes } from '../constants/hsvideo';
import { INSERT_HUBSPOT_VIDEO, EDIT_HUBSPOT_VIDEO } from '../constants/usageTracking';
import { trackInteraction } from '../utils/usageTracking';
import { serializeConversionAsset, VideoNodeAttributes, getVideoParamsFromHtml, getIframeUrl, mceAttribPrefix, retainAttributesOfReplacedVideo } from './util';
import { getAlignment, resizeAssetDimensions } from '../utils/shared';
function trackVideoInsert(editor, type, isVideoReplaced = false) {
  trackInteraction(editor, 'tinymceInsertVideo', INSERT_HUBSPOT_VIDEO, {
    type,
    replaced: isVideoReplaced,
    version: 'contextualToolbar'
  });
}
function _trackVideoEdit(editor, editType) {
  trackInteraction(editor, 'tinymceEditVideo', EDIT_HUBSPOT_VIDEO, {
    editType,
    type: 'hubspot-video-2',
    version: 'contextualToolbar'
  });
}
const trackVideoEdit = debounce(_trackVideoEdit, 400);
const handleInsertHtmlVideo = (editor, file) => {
  const {
    dom,
    undoManager
  } = editor;
  const {
    width,
    height
  } = file;
  // arbitrary MAX width
  const maxWidth = 600;
  let _width = width;
  let _height = height;
  if (width > maxWidth) {
    _width = maxWidth;
    _height = Math.round(height * (maxWidth / width));
  }
  const videoAttributes = {
    src: file.default_hosting_url,
    width: _width,
    height: _height,
    controls: true,
    style: `width: ${_width}px; height: ${_height}px;`
  };
  editor.focus();
  const videoEl = dom.createHTML('video', videoAttributes);
  undoManager.transact(() => {
    editor.insertContent(videoEl);
    editor.execCommand('hsInsertNewline', false, {
      target: videoEl
    });
  });
  trackVideoInsert(editor, 'html-video-tag');
};
const handleInsertHubLVideo = (editor, file, videoToReplace) => {
  const isVideo2 = isHubSpotVideo2(file);
  editor.focus();
  editor.undoManager.transact(() => {
    let videoParams = getHubLVideoParamObjectFromFile(file);
    if (videoToReplace) {
      const retainedParams = retainAttributesOfReplacedVideo(videoToReplace, videoParams.width, videoParams.height);
      videoParams = Object.assign({}, videoParams, retainedParams);
    }
    if (isVideo2) {
      videoParams.type = HublVideoTypes.HSVIDEO2;
    }
    const hublVideo = getHubLVideoHubLTag(videoParams);
    if (videoToReplace) {
      const bm = editor.selection.getBookmark();
      editor.selection.setContent('');
      editor.insertContent(hublVideo);
      editor.selection.moveToBookmark(bm);
    } else {
      editor.insertContent(hublVideo);
      editor.execCommand('hsInsertNewline', false, {
        target: editor.selection.getNode()
      });
    }
  });
  const videoType = isVideo2 ? 'hubspot-video-2' : 'hubspot-video-player';
  trackVideoInsert(editor, videoType, !!videoToReplace);
};
export const getInsertVideoHandler = (editor, videoToReplace) => file => {
  if (isHubLVideo(file)) {
    handleInsertHubLVideo(editor, file, videoToReplace);
  } else {
    handleInsertHtmlVideo(editor, file);
  }
};
export const getUpdateVideoParams = editor => (target, videoParams) => {
  editor.undoManager.transact(() => {
    const videoParamAttribs = Object.keys(videoParams).reduce((acc, param) => {
      acc[`${mceAttribPrefix}${param}`] = videoParams[param];
      return acc;
    }, {});
    editor.dom.setAttribs(target, videoParamAttribs);
    const video = Object.assign({}, getVideoParamsFromHtml(target, mceAttribPrefix), videoParams);
    // update src to actually show updates on video preview
    const $iframe = editor.$('iframe', target);
    $iframe.attr('src', getIframeUrl(video, editor));
  });
  trackVideoEdit(editor, 'videoParams');
};
export const getUpdateConversionAssetHandler = editor => (target, conversionAsset) => {
  editor.undoManager.transact(() => {
    let video = getVideoParamsFromHtml(target, mceAttribPrefix);
    let nextConversionAsset = null;
    if (conversionAsset) {
      const {
        conversion_asset: prevConversionAsset
      } = video;
      if (!prevConversionAsset) {
        nextConversionAsset = conversionAsset;
      } else {
        nextConversionAsset = Object.assign({}, prevConversionAsset, conversionAsset);
      }
      if (!nextConversionAsset.position) {
        nextConversionAsset.position = VideoOverlayPositions.AFTER;
      }
    }
    video = Object.assign({}, video, {
      conversion_asset: nextConversionAsset
    });
    editor.dom.setAttrib(target, VideoNodeAttributes.CONVERSION_ASSET, serializeConversionAsset(nextConversionAsset));
    // update src to actually show updates on video preview
    const $iframe = editor.$('iframe', target);
    $iframe.attr('src', getIframeUrl(video, editor));
  });
  trackVideoEdit(editor, 'conversionAsset');
};
export const getRemoveVideoHandler = editor => video => {
  editor.undoManager.transact(() => {
    editor.dom.remove(video);
    editor.selection.collapse();
    editor.nodeChanged();
  });
  trackVideoEdit(editor, 'removeVideo');
};
export const getPlayerColorChangeHandler = editor => (target, color) => {
  const dom = editor.dom;
  const trimmedColor = color && color.substr(1); // [color] will be null if "reset to default" was clicked
  const video = Object.assign({}, getVideoParamsFromHtml(target, mceAttribPrefix), {
    color: trimmedColor,
    play_button_color: trimmedColor
  });
  editor.undoManager.transact(() => {
    // update params on element to preserve parsing to HubL
    dom.setAttrib(target, VideoNodeAttributes.COLOR, trimmedColor);
    dom.setAttrib(target, VideoNodeAttributes.PLAY_BUTTON_COLOR, trimmedColor);

    // update src to actually show updates on video preview
    const $iframe = editor.$('iframe', target);
    $iframe.attr('src', getIframeUrl(video, editor));
  });
  trackVideoEdit(editor, 'playerColor');
};
export const getAlignmentChangeHandler = editor => (target, alignment = Alignments.NONE, prevAlignment) => {
  if (target.classList.contains('mce-preview-object') || target.classList.contains('hs-video') || target.classList.contains('mce-object')) {
    editor.undoManager.transact(() => {
      if (alignment === prevAlignment) {
        alignment = Alignments.NONE;
      }
      const alignStyles = STYLE_OPTIONS[alignment];
      if (alignStyles) {
        editor.dom.setStyles(target, alignStyles);
      }
    });
    trackVideoEdit(editor, 'alignment');
  }
};
export const getDimensionsChangeHandler = (editor, maxContainerWidth) => (target, dimensions) => {
  const {
    width,
    height
  } = resizeAssetDimensions(maxContainerWidth, dimensions.width, dimensions.height);
  editor.undoManager.transact(() => {
    if (width && height) {
      editor.dom.setStyles(target, {
        'max-width': `${width}px`,
        'max-height': `${height}px`
      });
      editor.dom.setAttribs(target, {
        [`${mceAttribPrefix}width`]: width,
        [`${mceAttribPrefix}height`]: height
      });
    }
    editor.selection.select(target);
  });
  trackVideoEdit(editor, 'dimensions');
};
export const getMarginChangeHandler = editor => (target, margin) => {
  const {
    top,
    left,
    right,
    bottom
  } = margin;
  editor.undoManager.transact(() => {
    editor.dom.setStyles(target, {
      'margin-top': top,
      'margin-left': left,
      'margin-right': right,
      'margin-bottom': bottom
    });
  });
  trackVideoEdit(editor, 'margin');
};
export const getSizeChangeHandler = editor => (target, size) => {
  editor.undoManager.transact(() => {
    let styles;
    let fullWidth;
    if (size === VideoSizes.FULL) {
      styles = {
        'max-width': null,
        'max-height': null,
        'margin-left': 'auto',
        'margin-right': 'auto',
        display: 'block',
        float: null
      };
      fullWidth = true;
    } else {
      styles = {
        'max-width': `${target.getAttribute(`${mceAttribPrefix}width`)}px`,
        'max-height': `${target.getAttribute(`${mceAttribPrefix}height`)}px`
      };
      fullWidth = false;
    }
    editor.dom.setStyles(target, styles);
    editor.dom.setAttrib(target, `${mceAttribPrefix}full_width`, fullWidth);
  });
  if (size === VideoSizes.FULL) {
    trackVideoEdit(editor, 'fullwidth');
  }
};
export function getLayoutChangeHandler(editor) {
  const changeAlignment = getAlignmentChangeHandler(editor);
  const changeSize = getSizeChangeHandler(editor);
  return (target, alignment) => {
    const curFullWidth = editor.dom.getAttrib(target, `${mceAttribPrefix}full_width`) === 'true';
    const curAlignment = curFullWidth ? null : getAlignment(target);
    if (alignment === Alignments.JUSTIFY) {
      changeSize(target, curFullWidth ? VideoSizes.CUSTOM : VideoSizes.FULL);
    } else {
      if (curFullWidth) {
        changeSize(target, VideoSizes.CUSTOM);
      }
      changeAlignment(target, alignment, curAlignment);
    }
  };
}